/**
 * Leitor de código USB ou Bluetooth — o Gertec do computador.
 *
 * O leitor se apresenta ao sistema como teclado: "digita" o código e aperta
 * Enter. Como no terminal Gertec, não há imagem para mostrar, só o texto que
 * chega pronto — e ele entra na tela pelo mesmo `receberCodigo` da câmera.
 *
 * O perigo está no "digita". O QR do ingresso é um JSON (`{"eventoId":...}`),
 * e o leitor manda teclas, não letras: se ele estiver no padrão americano e o
 * Windows em ABNT2, as aspas saem como acento morto, os dois-pontos como "Ç",
 * e o ingresso certo é recusado como código inválido com a fila olhando. Por
 * isso cada tecla é guardada duas vezes — a letra que o sistema entregou e a
 * tecla física — e `montarLeitura` escolhe a versão que faz sentido.
 */

import {useEffect, useRef} from 'react';

import {tecladoFisico} from '../ui/tecladoFisico';

export interface Tecla {
  /** O que o sistema entregou (`KeyboardEvent.key`), já no layout do Windows. */
  letra: string;
  /** A tecla física (`KeyboardEvent.code`), igual em qualquer layout. */
  fisica: string;
  shift: boolean;
}

/** A tecla física no padrão americano: [sem shift, com shift]. */
const PADRAO_AMERICANO: Record<string, [string, string]> = {
  Backquote: ['`', '~'],
  Minus: ['-', '_'],
  Equal: ['=', '+'],
  BracketLeft: ['[', '{'],
  BracketRight: [']', '}'],
  Backslash: ['\\', '|'],
  Semicolon: [';', ':'],
  Quote: ["'", '"'],
  Comma: [',', '<'],
  Period: ['.', '>'],
  Slash: ['/', '?'],
  Space: [' ', ' '],
  NumpadDecimal: ['.', '.'],
  NumpadAdd: ['+', '+'],
  NumpadSubtract: ['-', '-'],
  NumpadMultiply: ['*', '*'],
  NumpadDivide: ['/', '/'],
};

const SIMBOLOS_DOS_DIGITOS = ')!@#$%^&*(';

function americana({fisica, shift, letra}: Tecla): string {
  const digito = fisica.match(/^Digit(\d)$/);
  if (digito) {
    return shift ? SIMBOLOS_DOS_DIGITOS[Number(digito[1])] : digito[1];
  }
  const numpad = fisica.match(/^Numpad(\d)$/);
  if (numpad) {
    return numpad[1];
  }
  const tecla = fisica.match(/^Key([A-Z])$/);
  if (tecla) {
    return shift ? tecla[1] : tecla[1].toLowerCase();
  }
  return PADRAO_AMERICANO[fisica]?.[shift ? 1 : 0] ?? (letra.length === 1 ? letra : '');
}

function ehObjetoJson(texto: string): boolean {
  try {
    const valor = JSON.parse(texto);
    return typeof valor === 'object' && valor !== null;
  } catch {
    return false;
  }
}

/**
 * O texto de uma leitura, a partir das teclas.
 *
 * Quando as duas versões concordam — código só de dígitos, ou leitor e
 * Windows no mesmo layout — não há o que decidir. Quando discordam, vale a
 * que for um JSON de verdade: é o único formato que tem caractere que muda de
 * lugar entre os layouts. Nenhuma das duas sendo JSON, fica a do sistema, que
 * é o que um teclado de verdade digitaria.
 */
export function montarLeitura(teclas: Tecla[]): string {
  const doSistema = teclas.map(t => (t.letra.length === 1 ? t.letra : '')).join('');
  const daTecla = teclas.map(americana).join('');

  if (doSistema === daTecla || ehObjetoJson(doSistema)) {
    return doSistema.trim();
  }
  if (ehObjetoJson(daTecla)) {
    return daTecla.trim();
  }
  return doSistema.trim();
}

/**
 * Este aparelho é um computador: a leitura é pelo leitor USB, e a tela vira o
 * totem do Gertec em vez de câmera.
 *
 * O critério é o ponteiro principal ser fino (mouse, touchpad). Notebook com
 * tela de toque continua computador; celular e tablet têm o dedo como
 * ponteiro principal e ficam com a câmera. `?leitor=camera` ou `?leitor=usb`
 * no endereço força um dos dois — e fica lembrado neste navegador.
 */
function decidirModoTerminal(): boolean {
  if (typeof window === 'undefined' || !window.location) {
    return false;
  }
  const CHAVE = 'tmobi.validador.leitor';
  const pedido = new URLSearchParams(window.location.search).get('leitor');

  try {
    if (pedido === 'usb' || pedido === 'camera') {
      localStorage.setItem(CHAVE, pedido);
    }
    const lembrado = localStorage.getItem(CHAVE);
    if (lembrado === 'usb' || lembrado === 'camera') {
      return lembrado === 'usb';
    }
  } catch {
    // Navegador sem armazenamento: fica o critério do ponteiro.
  }
  return window.matchMedia?.('(pointer: fine)').matches ?? false;
}

export const MODO_TERMINAL = decidirModoTerminal();

console.log(`[leitura] fonte: ${MODO_TERMINAL ? 'leitor USB' : 'câmera'}`);

/**
 * Entre duas teclas do leitor passam milissegundos; entre duas de gente,
 * centenas. Pausa maior que esta descarta o que veio antes — uma tecla
 * perdida não pode virar prefixo do próximo ingresso.
 */
const PAUSA_MAXIMA_MS = 300;

const SO_MODIFICADOR = new Set(['Shift', 'Control', 'Alt', 'AltGraph', 'Meta', 'CapsLock', 'NumLock']);

function emCampoDeTexto(alvo: EventTarget | null): boolean {
  const el = alvo as HTMLElement | null;
  return Boolean(el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable));
}

/**
 * Menor rajada que conta como leitura quando a leitura está pausada. Código
 * de ingresso tem oito caracteres ou mais; gente apertando Enter num botão
 * não chega perto disso em 300 ms.
 */
const RAJADA_MINIMA = 4;

/**
 * Escuta o leitor e entrega cada código a `aoLer` enquanto `ativo`.
 *
 * Com a leitura pausada (desfecho na tela, conferência de nominal, histórico
 * aberto) o ouvinte continua ligado, só que para descartar: o leitor termina
 * cada código com Enter, e o Enter vai para o botão que estiver com foco. Na
 * conferência do nominal esse botão pode ser o "Liberar" — e o próximo da fila
 * passando o ingresso liberaria o anterior sem ninguém olhar o documento. É o
 * mesmo que o app faz ao ignorar leitura com desfecho na tela; aqui ignorar
 * exige engolir a tecla.
 *
 * Fica ligado também no celular: leitor Bluetooth pareado chega do mesmo jeito,
 * e a câmera continua funcionando ao lado. Campo de texto com foco e o
 * teclado do "Digitar" são deixados em paz — o leitor digita neles como
 * qualquer teclado.
 */
export function useLeitorUsb(ativo: boolean, aoLer: (codigo: string) => void): void {
  const ouvinte = useRef(aoLer);
  ouvinte.current = aoLer;
  const lendo = useRef(ativo);
  lendo.current = ativo;

  useEffect(() => {
    let teclas: Tecla[] = [];
    let ultima = 0;

    const aoTeclar = (e: KeyboardEvent) => {
      if (emCampoDeTexto(e.target) || tecladoFisico.ocupadoPelaTela() || e.ctrlKey || e.metaKey || e.altKey) {
        return;
      }

      const agora = performance.now();
      if (agora - ultima > PAUSA_MAXIMA_MS) {
        teclas = [];
      }
      ultima = agora;

      if (e.key === 'Enter' || e.key === 'Tab') {
        const rajada = teclas;
        teclas = [];

        if (lendo.current && rajada.length > 0) {
          // O Enter é do leitor, não do botão que ficou com foco depois do
          // último clique — sem isto ele "clicaria" de novo no botão.
          e.preventDefault();
          e.stopPropagation();
          const codigo = montarLeitura(rajada);
          if (codigo) {
            ouvinte.current(codigo);
          }
        } else if (rajada.length >= RAJADA_MINIMA) {
          e.preventDefault();
          e.stopPropagation();
          console.log(`[leitura] leitor ignorado com a leitura pausada: ${montarLeitura(rajada)}`);
        }
        return;
      }

      if (SO_MODIFICADOR.has(e.key)) {
        return;
      }

      // O acento morto não entra na letra, mas a tecla física dele conta.
      teclas.push({letra: e.key === 'Dead' ? '' : e.key, fisica: e.code, shift: e.shiftKey});

      // Espaço rolaria a página (ou apertaria o botão com foco), e letra pode
      // ser atalho do navegador: enquanto a portaria lê, o teclado é do leitor.
      if (lendo.current || teclas.length > 1) {
        e.preventDefault();
      }
    };

    // Na captura, antes de qualquer botão da tela ver a tecla.
    window.addEventListener('keydown', aoTeclar, {capture: true});
    return () => window.removeEventListener('keydown', aoTeclar, {capture: true});
  }, []);
}
